import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Documents from "../../common/Documents/Documents";
import Parts from "../../common/Parts/Parts";
import Sidebar from "../../layout/Sidebar/Sidebar";
import Header from "../../layout/Header/Header";
const SearchPage   = lazy(() => import("../SearchPage/SearchPage"));
const SetupPage    = lazy(() => import("../SetupPage/SetupPage"));
const DocumentPage = lazy(() => import("../DocumentPage/DocumentPage"));
const SettingsPage = lazy(() => import("../SettingsPage/SettingsPage"));
import { getAPIBaseURL } from "../../../utils/api";
import { apiGet } from "../../../utils/apiClient";
import OfflineBanner from "../../../offline/OfflineBanner";
import OnlineOnly from "../../../offline/OnlineOnly";
import RequireUpload from "../../common/RequireUpload/RequireUpload";
import SwUpdateToast from "../../../offline/SwUpdateToast";
import { capture } from "../../../utils/analytics";
import Chat from "../../common/Chat/Chat";
import DocumentViewerOverlay from "../../common/DocumentViewerOverlay/DocumentViewerOverlay";
import { useUploadStatus } from "../../../utils/UploadStatusContext";
import "./App.css";

const API_BASE_URL = getAPIBaseURL();

const BreadcrumbHomeIcon = () => (
  <svg
    className="breadcrumb-icon-svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.5 10.5L12 4L19.5 10.5V19.5C19.5 20.3284 18.8284 21 18 21H15V15H9V21H6C5.17157 21 4.5 20.3284 4.5 19.5V10.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Self-healing for stale lazy-chunk URLs after a deploy. When the user has a
// tab open from before a deploy and navigates to a lazy route after the
// deploy, Vite's hash-rotated chunk URL 404s and the dynamic import throws.
// Reload the page once so the new build's chunk paths are picked up.
// Offline guard: reload at most once per tab session, otherwise an uncached
// chunk would loop the page forever with the backend unreachable.
const CHUNK_RELOAD_KEY = 'od.chunkReloaded';
class ChunkErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) {
    if (!/Loading chunk|Failed to fetch dynamically imported module/i.test(err?.message || '')) return;
    let reloadedBefore = false;
    try { reloadedBefore = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1'; } catch { /* ignore */ }
    if (reloadedBefore) return;
    try { sessionStorage.setItem(CHUNK_RELOAD_KEY, '1'); } catch { /* ignore */ }
    window.location.reload();
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="main-field" role="alert">
        <p>Diese Ansicht konnte nicht geladen werden. Offline ist sie erst nach einer erneuten Online-Nutzung verfügbar.</p>
      </div>
    );
  }
}

const App = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedPart, setSelectedPart] = useState("1");
  const [parts, setParts] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [sortField, setSortField] = useState("displayName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isLoading, setIsLoading] = useState(true);
  const [documentViewerState, setDocumentViewerState] = useState(null);
  // Auf schmalen Viewports (≤768px, Chat = Vollbild-Overlay) geschlossen starten
  const [isChatOpen, setIsChatOpen] = useState(() => window.innerWidth > 768);
  const mainRef = useRef(null);
  const headerRef = useRef(null);

  const { uploadStatus } = useUploadStatus();
  const prevCompletedRef = useRef(false);

  useEffect(() => {
    fetchParts();
  }, []);

  useEffect(() => {
    if (documentViewerState) {
      capture("document_viewer_opened", {
        type: documentViewerState.type,
        displayName: documentViewerState.displayName,
        page: documentViewerState.page,
      });
    }
  }, [documentViewerState]);

  useEffect(() => {
    // console.log(`Refreshing documents for part ${selectedPart}...`);
    refreshDocuments();
  }, [selectedPart]);

  const location = useLocation();

  useEffect(() => {
    // console.log("Location changed:", location);
    if (location.pathname === "/") {
      // console.log(
      //   "Fetching parts and refreshing documents due to route change..."
      // );
      fetchParts();
      refreshDocuments();
    }
  }, [location]);

  useEffect(() => {
    const newBreadcrumbs = constructBreadcrumb(selectedPart, parts);
    // console.log("New breadcrumbs:", newBreadcrumbs);
    setBreadcrumbs(newBreadcrumbs);
  }, [selectedPart, parts]);

  // Auto-refresh documents when upload completes
  // This ensures documents are updated even after page refresh during processing
  useEffect(() => {
    const wasCompleted = prevCompletedRef.current;
    const isCompleted = uploadStatus?.completed === true;

    // Only refresh when transitioning from not-completed to completed
    if (!wasCompleted && isCompleted) {
      console.log('[App] Upload completed, refreshing documents...');
      refreshDocuments();
    }

    // Update ref for next comparison
    prevCompletedRef.current = isCompleted;
  }, [uploadStatus?.completed]);

  // Calculate exact panel height when PDF viewer is open

  const fetchDocumentsByPart = (partId) => {
    if (!partId) {
      console.error("Invalid partId:", partId);
      return;
    }

    setIsLoading(true);

    // console.log("Fetching documents for part:", partId);
    apiGet(`/api/documents?part_id=${encodeURIComponent(partId)}`)
      .then((data) => {
        if (data.error) {
          console.error("Error fetching documents:", data.error);
        } else {
          // console.log("Documents fetched successfully");
          const fetchedDocuments = data.map((doc) => ({
            ...doc,
            doctype: doc.doctype || "DefaultType",
          }));

          const sortedDocuments = [...fetchedDocuments].sort((a, b) => {
            const valueA = a.displayName ? a.displayName.toUpperCase() : "";
            const valueB = b.displayName ? b.displayName.toUpperCase() : "";
            return valueA.localeCompare(valueB);
          });

          setDocuments(sortedDocuments);
        }

        setIsLoading(false);
      })
      .catch((error) => console.error("Error fetching documents:", error));
  };

  const getPartNameById = (partId) => {
    const part = parts.find((part) => part.id === partId);
    return part ? part.name : "";
  };

  const fetchParts = () => {
    // console.log("Fetching parts...");
    return apiGet("/api/parts")
      .then((partsData) => {
        setParts(partsData);
        if (!selectedPart) {
          const defaultPart = partsData.find((part) => part.id === "1");
          if (defaultPart) {
            setSelectedPart(defaultPart.id);
          }
        }
        return partsData;
      })
      .catch((error) => {
        console.error("Error fetching parts:", error);
        return [];
      });
  };

  const handlePartUpdated = (deletedPartId) => {
    fetchParts().then((newParts) => {
      const currentSelectedPart = selectedPart;
      const partExists = newParts.some(
        (part) => part.id === currentSelectedPart
      );
      if (!partExists) {
        setSelectedPart("1");
        const newBreadcrumbs = constructBreadcrumb("1", newParts);
        setBreadcrumbs(newBreadcrumbs);
      }
    });
  };

  const constructBreadcrumb = (partId, parts) => {
    const breadcrumb = [];
    let currentPartId = partId;

    while (currentPartId) {
      const currentPart = parts.find((part) => part.id === currentPartId);
      if (currentPart) {
        breadcrumb.unshift({ id: currentPart.id, name: currentPart.name });
        currentPartId = currentPart.parent_id;
      } else {
        break;
      }
    }

    return breadcrumb;
  };

  const onBreadcrumbClick = (breadcrumbId) => {
    // console.log("Breadcrumb clicked:", breadcrumbId);
    const breadcrumbIndex = breadcrumbs.findIndex((b) => b.id === breadcrumbId);
    const newBreadcrumbs = breadcrumbs.slice(0, breadcrumbIndex + 1);
    setBreadcrumbs(newBreadcrumbs);
    setSelectedPart(breadcrumbId);
  };

  const onSelectPart = (partId) => {
    // console.log("Selecting part:", partId);
    const newBreadcrumbs = constructBreadcrumb(partId, parts);
    setBreadcrumbs(newBreadcrumbs);
    setSelectedPart(partId);
  };

  const refreshDocuments = () => {
    // console.log("Refreshing documents for part:", selectedPart);
    return fetchDocumentsByPart(selectedPart);
  };

  const handleRemoveDocument = (documentName, docId) => {
    // console.log(`Attempting to remove document: ${documentName}`);
    fetch(`${API_BASE_URL}/api/remove/${encodeURIComponent(documentName)}`, {
      method: `DELETE`,
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          setDocuments((prevDocs) =>
            prevDocs.filter((doc) => docId ? (doc.docId || doc._id) !== docId : doc.name !== documentName)
          );
        } else {
          console.error(
            `Failed to remove document: ${documentName}, Response status: ${response.status}`
          );
        }
      })
      .catch((error) => {
        console.error(`Error removing document: ${documentName}`, error);
      });
  };

  return (
    <div className={`app${isChatOpen ? ' chat-is-open' : ''}`}>
      <Sidebar
        setSelectedPart={setSelectedPart}
        partName={getPartNameById("1")}
        forceCollapsed={false}
        onNavigate={() => setDocumentViewerState(null)}
      />
      <div ref={mainRef} className="main">
        <Header ref={headerRef} />
        <OfflineBanner />
        <ChunkErrorBoundary>
        <Suspense fallback={
          <div className="main-field" aria-busy="true">
            <div className="loading-spinner" />
          </div>
        }>
        <Routes>
          <Route
            path="/"
            element={
              <React.Fragment>
                  {/* Navigation strip — breadcrumbs + parts (not in a card) */}
                  <div className="nav-strip">
                    <div
                      className="breadcrumbs"
                      role="navigation"
                      aria-label="Navigationspfad"
                    >
                      <div className="breadcrumb-track">
                        {breadcrumbs.length > 0 ? (
                          breadcrumbs.map((breadcrumb, index) => {
                            const isActive = index === breadcrumbs.length - 1;
                            return (
                              <React.Fragment key={breadcrumb.id}>
                                <button
                                  type="button"
                                  className={`breadcrumb-item ${
                                    isActive ? "is-active" : ""
                                  }`}
                                  onClick={
                                    !isActive
                                      ? () => onBreadcrumbClick(breadcrumb.id)
                                      : undefined
                                  }
                                  disabled={isActive}
                                  aria-current={isActive ? "page" : undefined}
                                  title={breadcrumb.name}
                                >
                                  {index === 0 && (
                                    <span
                                      className="breadcrumb-icon"
                                      aria-hidden="true"
                                    >
                                      <BreadcrumbHomeIcon />
                                    </span>
                                  )}
                                  <span className="breadcrumb-label">
                                    {breadcrumb.name}
                                  </span>
                                </button>
                                {!isActive && (
                                  <span
                                    className="breadcrumb-divider"
                                    aria-hidden="true"
                                  >
                                    /
                                  </span>
                                )}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <span className="breadcrumb-empty">
                            Keine Navigation verfügbar
                          </span>
                        )}
                      </div>
                    </div>
                    <Parts
                      parts={parts}
                      onSelectPart={onSelectPart}
                      selectedPart={selectedPart}
                      onPartUpdated={handlePartUpdated}
                      onDocumentsUpdated={refreshDocuments}
                    />
                  </div>

                  {/* Documents content card */}
                  <div className="main-field">
                    <Documents
                      documents={documents}
                      setDocuments={setDocuments}
                      onRemoveDocument={handleRemoveDocument}
                      refreshDocuments={refreshDocuments}
                      sortField={sortField}
                      setSortField={setSortField}
                      sortOrder={sortOrder}
                      setSortOrder={setSortOrder}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                      parts={parts}
                      onOpenDocument={setDocumentViewerState}
                      hidePartFilter={true}
                      hideComponentColumn={true}
                    />
                  </div>

              </React.Fragment>
            }
          />
          <Route
            path="/route/searchpage"
            element={
              <SearchPage
                onOpenDocument={(opts) => setDocumentViewerState(opts)}
                onCloseDocument={() => setDocumentViewerState(null)}
              />
            }
          />
          <Route
            path="/route/setup"
            element={
              <OnlineOnly message="Einrichtung und Upload sind nur online verfügbar">
                <RequireUpload>
                  <div className="main-field">
                    <SetupPage />
                  </div>
                </RequireUpload>
              </OnlineOnly>
            }
          />
          <Route
            path="/route/documents"
            element={
              <div className="main-field">
                <DocumentPage
                  onRemoveDocument={handleRemoveDocument}
                  refreshDocuments={refreshDocuments}
                  selectedPart={selectedPart}
                  onOpenDocument={setDocumentViewerState}
                />
              </div>
            }
          />
          <Route
            path="/route/settings"
            element={
              <SettingsPage
                refreshDocuments={refreshDocuments}
                parts={parts}
                onPartUpdated={handlePartUpdated}
                onDocumentsUpdated={refreshDocuments}
              />
            }
          />
        </Routes>
        </Suspense>
        </ChunkErrorBoundary>
      </div>
      {documentViewerState && (
        <DocumentViewerOverlay
          viewerState={documentViewerState}
          onClose={() => setDocumentViewerState(null)}
        />
      )}
      <div className="chat-field">
        <Chat setDocumentViewerState={setDocumentViewerState} onChatToggle={setIsChatOpen} />
      </div>
      <SwUpdateToast />
    </div>
  );
};

export default App;
